"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Landing v2 hero — "Organic Editorial" direction (landing-page-guide-v2).
 *
 * DESIGN SYSTEM (documented per skill step 1):
 *  - Display: Fraunces (optical serif, food-editorial character) — kinetic
 *    word-by-word entrance with a hand-drawn lime underline on the promise.
 *  - Body: Manrope. Palette: the brand's own cream/green/lime/coral.
 *  - Composition: the owner-preferred main.html triptych RESTORED and
 *    elevated — browser frame (week plan) + notched phone (Amy chat) +
 *    rotated savings float — now depth-layered with pointer parallax
 *    (three depths), slow counter-phase idle bobs, and premium DishArt
 *    illustration instead of mock plates. Honest: "Sample plan" badge,
 *    numbers internally consistent with the demo/savings sections
 *    (€16.20 week, €3.80/€3.20 meals).
 *  - Texture: SVG grain overlay + soft lime/coral radial washes — never
 *    flat white. All motion behind useReducedMotion.
 */

import { useRef } from "react"
import { motion, useMotionValue, useReducedMotion, useSpring, useTransform } from "framer-motion"
import { ClipboardCheck, Repeat } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useI18n } from "@/i18n"
import { BILLING_MODE } from "@/lib/billingMode"
import type { AuthMode } from "@/components/auth/authMode"
import { BrandMarkV2 } from "./BrandMarkV2"
import { AppStoreLink } from "./AppStoreLink"
import appPlanEn from "@/assets/marketing/app-plan-en.png"
import appPlanDe from "@/assets/marketing/app-plan-de.png"

interface HeroV2Props {
  onOpenAuth: (mode: AuthMode) => void
}

/** Split the localized tagline into animatable words, underlining the middle sentence. */
function KineticHeadline({ text, reduce }: { text: string; reduce: boolean }) {
  const words = text.split(" ")
  let sentence = 0
  return (
    <h1 className="font-display text-[2.9rem] leading-[1.04] font-semibold tracking-tight text-ink sm:text-6xl lg:text-[4.4rem]">
      {words.map((w, i) => {
        const cls = sentence === 1 ? "bbv2-underline" : undefined
        const el = (
          <span key={i} className="inline-block overflow-hidden pb-1 align-bottom">
            <motion.span
              className={`inline-block ${cls ?? ""}`}
              initial={reduce ? false : { y: "110%" }}
              animate={{ y: 0 }}
              transition={{ delay: 0.08 + i * 0.07, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            >
              {w}
              {i < words.length - 1 ? " " : ""}
            </motion.span>
          </span>
        )
        if (w.endsWith(".")) sentence += 1
        return el
      })}
    </h1>
  )
}

export function HeroV2({ onOpenAuth }: HeroV2Props) {
  const { t, locale } = useI18n()
  const appStore = BILLING_MODE === "app_store"
  const reduce = useReducedMotion() ?? false
  const stageRef = useRef<HTMLDivElement>(null)

  // Pointer parallax: -0.5..0.5 across the visual stage, spring-smoothed.
  const px = useMotionValue(0)
  const py = useMotionValue(0)
  const sx = useSpring(px, { stiffness: 60, damping: 18 })
  const sy = useSpring(py, { stiffness: 60, damping: 18 })
  const browserX = useTransform(sx, v => v * -10)
  const browserY = useTransform(sy, v => v * -6)
  const phoneX = useTransform(sx, v => v * 22)
  const phoneY = useTransform(sy, v => v * 14)
  const floatX = useTransform(sx, v => v * 14)
  const floatY = useTransform(sy, v => v * 10)

  function onPointerMove(e: React.PointerEvent) {
    if (reduce || !stageRef.current) return
    const r = stageRef.current.getBoundingClientRect()
    px.set((e.clientX - r.left) / r.width - 0.5)
    py.set((e.clientY - r.top) / r.height - 0.5)
  }

  const idle = (phase: number) =>
    reduce
      ? undefined
      : {
          y: [0, -7, 0],
          transition: {
            duration: 7 + phase,
            repeat: Infinity,
            ease: "easeInOut" as const,
            delay: phase,
          },
        }

  return (
    <section
      id="top"
      className="bbv2-grain relative scroll-mt-[72px] overflow-hidden border-b border-line bg-cream"
    >
      {/* Atmosphere: soft washes, never flat. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 right-[-10%] size-[620px] rounded-full bg-lime/30 blur-[110px]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute bottom-[-20%] left-[-8%] size-[420px] rounded-full bg-coral/15 blur-[90px]"
      />

      {/* P1 fix 2026-09-10: the stage reserves 200px of bottom padding so the two
          floats hang clear of the browser card, and that padding sits INSIDE this grid
          row — the hero had grown to 861px, taller than a 14-inch laptop viewport
          (1512x860), so the composition could not be seen at once and any scroll slid
          the headline under the sticky header. The floats keep their positions; the
          height comes back out of the section padding they already fill visually. */}
      <div className="relative mx-auto grid max-w-6xl gap-14 px-4 pb-20 pt-14 md:grid-cols-[1fr_1fr] md:items-center md:px-8 md:pb-4 md:pt-10">
        {/* Copy column */}
        <div className="flex flex-col items-start gap-7">
          <motion.span
            initial={reduce ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 rounded-bb-full border border-green/20 bg-paper px-4 py-1.5 text-sm font-semibold text-green"
          >
            <BrandMarkV2 size={18} />
            {t("landingPage.hero.pill")}
          </motion.span>

          <KineticHeadline text={t("landing.tagline")} reduce={reduce} />

          <motion.p
            initial={reduce ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.6 }}
            className="max-w-prose text-lg leading-relaxed text-ink-2"
          >
            {t("landingPage.hero.body")}
          </motion.p>

          <motion.div
            initial={reduce ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.6 }}
            className="flex flex-col gap-3"
          >
            <div className="flex flex-wrap items-center gap-3">
              <Button
                size="lg"
                onClick={() => onOpenAuth("signUp")}
                className="bbv2-cta group h-14 rounded-bb-full px-9 text-lg font-bold shadow-xl shadow-green/25 transition-transform hover:scale-[1.04]"
              >
                {t(appStore ? "landingPage.hero.ctaPrimaryAppStore" : "landingPage.hero.ctaPrimary")}
                <span
                  aria-hidden="true"
                  className="ml-1 inline-block transition-transform group-hover:translate-x-1"
                >
                  →
                </span>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="h-14 rounded-bb-full px-7 text-lg"
              >
                <a href="#how">{t("landingPage.hero.ctaSecondary")}</a>
              </Button>
              {/* app_store mode only (renders nothing otherwise); stacks under the CTAs on phones. */}
              <AppStoreLink placement="hero" className="h-14 w-full px-7 text-lg sm:w-auto" />
            </div>
            <p className="text-xs text-muted-foreground">{t(appStore ? "landingPage.hero.microProofAppStore" : "landingPage.hero.microProof")}</p>
          </motion.div>
        </div>

        {/* Visual stage: the restored triptych, depth-layered.
            Parity correction 2026-09-10 — the three layers and their motion are
            unchanged; what they SHOW is now the real product. Layer 1 is the web
            Today surface (real MealCard anatomy, no per-meal price, no dish
            artwork — debt 37), layer 2 is a real render of the accepted iPhone
            client (ADR-069), layer 3 is the value figure with the product's own
            Estimated marker and nothing else. Craft fix C2: the floats hang
            BELOW the card at md+ and stack under it on a phone, so no layer ever
            covers another. Craft fix C10: the stage is a real `role="img"` with
            one label instead of an aria-label on a plain div. */}
        <div
          ref={stageRef}
          onPointerMove={onPointerMove}
          role="img"
          aria-label={t("landingPage.heroV2.stageAria")}
          className="relative mx-auto w-full max-w-[540px] select-none pt-6 md:pt-0 md:pb-[176px]"
        >
          {/* Layer 1 — the real web Today, curated to two meals */}
          <motion.div
            style={reduce ? undefined : { x: browserX, y: browserY }}
            initial={reduce ? false : { opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative overflow-hidden rounded-bb-xl border border-line bg-paper shadow-2xl shadow-ink/10"
          >
            <div className="flex items-center gap-2 border-b border-line bg-cream/80 px-4 py-2.5">
              <span className="size-2.5 rounded-full bg-coral/70" />
              <span className="size-2.5 rounded-full bg-gold/70" />
              <span className="size-2.5 rounded-full bg-green/50" />
              <span className="ml-3 flex-1 truncate rounded-bb-full bg-paper px-3 py-1 text-[11px] text-muted-foreground">
                bitebalance.de
              </span>
              <span className="rounded-bb-full bg-lime/50 px-2.5 py-0.5 text-[10px] font-bold text-ink">
                {t("landingPage.hero.sampleBadge")}
              </span>
            </div>
            <div className="p-4 sm:p-5">
              <p className="text-[11px] font-bold tracking-[0.13em] text-green-2 uppercase">
                {t("landingPage.heroV2.cardDate")}
              </p>
              <p className="font-display mt-1 text-2xl font-semibold text-ink">
                {t("landingPage.heroV2.cardGreeting")}
              </p>
              <p className="mt-1 text-xs text-ink-2">{t("landingPage.heroV2.cardSubline")}</p>

              <article className="mt-3.5 rounded-bb-xl border border-line bg-paper p-3.5 shadow-sm">
                <p className="text-[11px] font-bold tracking-wide text-green-2 uppercase">
                  {t("landingPage.heroV2.cardLunchSlot")}
                </p>
                <h3 className="text-base leading-snug font-semibold text-ink">
                  {t("landingPage.heroV2.cardLunchTitle")}
                </h3>
                <ul className="mt-2 flex flex-wrap gap-1.5">
                  {[t("landingPage.heroV2.cardReason1"), t("landingPage.heroV2.cardReason2")].map(r => (
                    <li
                      key={r}
                      className="rounded-bb-full border border-line bg-cream px-2 py-0.5 text-xs text-ink-2"
                    >
                      {r}
                    </li>
                  ))}
                </ul>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <span className="rounded-bb-full border border-green/25 bg-lime/35 px-2.5 py-0.5 text-xs font-semibold text-ink">
                    {t("landingPage.heroV2.cardLunchKcal")}
                  </span>
                  <span className="rounded-bb-full border border-line bg-cream px-2.5 py-0.5 text-xs text-ink-2">
                    {t("landingPage.heroV2.cardLunchTime")}
                  </span>
                </div>
                <div className="mt-3 flex items-center gap-1.5">
                  <span className="min-h-9 flex-1 rounded-bb-lg border border-line bg-paper py-1.5 text-center text-[13px] font-semibold text-ink">
                    {t("landingPage.heroV2.cardOpenRecipe")}
                  </span>
                  <span
                    aria-hidden="true"
                    className="flex size-9 shrink-0 items-center justify-center rounded-bb-lg border border-line bg-paper"
                  >
                    <Repeat className="size-4 text-ink-2" />
                  </span>
                  <span
                    aria-hidden="true"
                    className="flex size-9 shrink-0 items-center justify-center rounded-bb-lg border border-line bg-paper"
                  >
                    <ClipboardCheck className="size-4 text-ink-2" />
                  </span>
                </div>
              </article>

              <article className="mt-2.5 rounded-bb-xl border border-line bg-paper p-3.5 shadow-sm">
                <p className="text-[11px] font-bold tracking-wide text-green-2 uppercase">
                  {t("landingPage.heroV2.cardDinnerSlot")}
                </p>
                <h3 className="text-base leading-snug font-semibold text-ink">
                  {t("landingPage.heroV2.cardDinnerTitle")}
                </h3>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <span className="rounded-bb-full border border-green/25 bg-lime/35 px-2.5 py-0.5 text-xs font-semibold text-ink">
                    {t("landingPage.heroV2.cardDinnerKcal")}
                  </span>
                  <span className="rounded-bb-full border border-line bg-cream px-2.5 py-0.5 text-xs text-ink-2">
                    {t("landingPage.heroV2.cardDinnerTime")}
                  </span>
                </div>
              </article>
            </div>
          </motion.div>

          {/* Layer 3 — value: label, figure, honesty marker. Nothing else. */}
          <motion.div
            style={reduce ? undefined : { x: floatX, y: floatY }}
            initial={reduce ? false : { opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.62, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mt-5 md:absolute md:bottom-8 md:-left-6 md:mt-0 md:w-[230px] md:-rotate-3"
          >
            <motion.div
              animate={idle(0)}
              className="rounded-bb-xl border border-line bg-paper p-4 shadow-xl shadow-ink/15"
            >
              <p className="text-[10px] font-bold tracking-[0.12em] text-ink-2 uppercase">
                {t("landingPage.heroV2.valueLabel")}
              </p>
              <p className="font-display mt-1 text-3xl leading-none font-semibold text-green">
                {t("landingPage.heroV2.valueAmount")}
              </p>
              <p className="mt-2.5">
                <span className="inline-flex items-center rounded-bb-full bg-gold/20 px-2 py-0.5 text-xs font-medium text-ink-2">
                  {t("landingPage.heroV2.valueEstimated")}
                </span>
              </p>
            </motion.div>
          </motion.div>

          {/* Layer 2 — a real render of the accepted iPhone client */}
          <motion.div
            style={reduce ? undefined : { x: phoneX, y: phoneY }}
            initial={reduce ? false : { opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            // `md:pb-[22px]` holds the space the removed caption occupied. On desktop this
            // block is bottom-anchored, so deleting a child below the phone would pull the
            // mockup 21px DOWN toward the hero's edge — the same direction as the P1 hero
            // clipping. The caption was text-xs (16px line box) + md:mt-1.5 (6px); reserving
            // those 22px keeps the phone pixel-identical to where it shipped.
            className="mt-5 flex items-center gap-4 md:absolute md:right-[-14px] md:bottom-0 md:mt-0 md:block md:w-[168px] md:rotate-3 md:pb-[22px]"
          >
            <motion.div animate={idle(0.6)} className="w-[150px] shrink-0 md:w-auto">
              <div className="rounded-[26px] border border-line bg-ink/95 p-1.5 shadow-2xl shadow-ink/25">
                <img
                  src={locale === "de" ? appPlanDe : appPlanEn}
                  alt={t("landingPage.heroV2.phoneAlt")}
                  width={780}
                  height={860}
                  loading="eager"
                  decoding="async"
                  className="block w-full rounded-[21px] bg-paper"
                />
              </div>
            </motion.div>
            {/* The "iPhone app · private beta" caption was removed 2026-09-11: it
                announced an availability that does not exist yet — TestFlight has not
                shipped. The mockup stays exactly where and as it was; only the claim is
                gone, and it is not replaced. */}
          </motion.div>
        </div>
      </div>
    </section>
  )
}
